import Vue from "vue";
import { http } from "@/config/httpCommon";

/*
* Admin managed custom fonts (US 6502).
* The endpoints live under the admin api base (VUE_APP_BASE_URL already ends with /api/admin),
* so the urls below stay relative exactly like every other module in this store.
*
* A font is ATTACHED to companies rather than owned by one: it can be attached to any number of
* companies, or marked global (is_global) which makes it available to every company. Attachments
* are editable after upload through `syncCustomFontCompanies`, so `company_id` on the listing is
* only a filter - omitting it returns every font.
*
* Detaching a company that still uses the font, and deleting a used font, are guarded server side:
* those endpoints answer 409 with `result.conflicts` unless `force` is set. The 409 is deliberately
* NOT swallowed here - the view needs it to show the affected products before retrying with force.
* */
export default {
    state: {
        customFonts: [],
        customFontsLoading: false,
        customFontsError: null,
    },
    mutations: {
        SET_CUSTOM_FONTS(state, payload) {
            state.customFonts = payload;
        },
        ADD_CUSTOM_FONT(state, payload) {
            state.customFonts.unshift(payload);
        },
        UPDATE_CUSTOM_FONT(state, payload) {
            const index = state.customFonts.findIndex(font => font.id == payload.id);
            if (index !== -1) {
                Vue.set(state.customFonts, index, payload);
            }
        },
        DELETE_CUSTOM_FONT(state, payload) {
            state.customFonts = state.customFonts.filter(font => font.id != payload);
        },
        SET_CUSTOM_FONTS_LOADING(state, payload) {
            state.customFontsLoading = payload;
        },
        SET_CUSTOM_FONTS_ERROR(state, payload) {
            state.customFontsError = payload;
        },
    },
    actions: {
        async getCustomFonts({ commit }, payload) {
            commit('SET_CUSTOM_FONTS_LOADING', true);
            commit('SET_CUSTOM_FONTS_ERROR', null);
            try {
                /* `order_id` widens the response to also include the fonts that order
                   actually references - including a customer's own upload, which the admin
                   library deliberately does not carry (scope = customer). The techpack
                   editor needs those so it can name them; the library screen leaves it out. */
                const params = {};
                if (payload && payload.company_id) {
                    params.company_id = payload.company_id;
                }
                if (payload && payload.order_id) {
                    params.order_id = payload.order_id;
                }
                let res = await http.get('custom-fonts', { params });
                commit('SET_CUSTOM_FONTS', res.data.result ? res.data.result : []);
                return res;
            } catch (error) {
                commit('SET_CUSTOM_FONTS_ERROR', error);
                throw error;
            } finally {
                commit('SET_CUSTOM_FONTS_LOADING', false);
            }
        },
        /*
        * payload: { file, display_name, family_name, is_global, company_ids, onProgress }
        * The endpoint accepts one single font file per request (ttf, otf, woff, woff2),
        * it is not the zip based "Data Source" font upload.
        * */
        async uploadCustomFont({ commit }, payload) {
            commit('SET_CUSTOM_FONTS_ERROR', null);
            let formData = new FormData();
            formData.append('file', payload.file);
            if (payload.display_name) {
                formData.append('display_name', payload.display_name);
            }
            if (payload.family_name) {
                formData.append('family_name', payload.family_name);
            }
            /* Platform admins choose the attachments explicitly: either the global flag or one
               or more company ids. Merchant admins pass neither and the API attaches the font to
               their own company - hence the presence checks instead of always appending. */
            if (payload.is_global !== undefined && payload.is_global !== null) {
                formData.append('is_global', payload.is_global ? '1' : '0');
            }
            if (Array.isArray(payload.company_ids)) {
                payload.company_ids.forEach(company_id => {
                    formData.append('company_ids[]', company_id);
                });
            }
            try {
                let res = await http.post('custom-fonts', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    },
                    onUploadProgress: (progress_event) => {
                        if (payload.onProgress && progress_event.total) {
                            payload.onProgress(Math.round((progress_event.loaded * 100) / progress_event.total));
                        }
                    }
                });
                commit('ADD_CUSTOM_FONT', res.data.result);
                return res;
            } catch (error) {
                commit('SET_CUSTOM_FONTS_ERROR', error);
                throw error;
            }
        },
        /*
        * payload: { id, display_name }
        * */
        async renameCustomFont({ commit }, payload) {
            commit('SET_CUSTOM_FONTS_ERROR', null);
            try {
                const body = { display_name: payload.display_name };
                if (payload.company_id) {
                    body.company_id = payload.company_id;
                }
                let res = await http.put(`custom-fonts/${payload.id}`, body);
                commit('UPDATE_CUSTOM_FONT', res.data.result);
                return res;
            } catch (error) {
                commit('SET_CUSTOM_FONTS_ERROR', error);
                throw error;
            }
        },
        /*
        * payload: { id, file, onProgress }
        * Replaces the file of an existing font, the font keeps its id and family name
        * and the version is bumped by the backend.
        * */
        async replaceCustomFontFile({ commit }, payload) {
            commit('SET_CUSTOM_FONTS_ERROR', null);
            let formData = new FormData();
            formData.append('file', payload.file);
            if (payload.company_id) {
                formData.append('company_id', payload.company_id);
            }
            try {
                let res = await http.post(`custom-fonts/${payload.id}/file`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    },
                    onUploadProgress: (progress_event) => {
                        if (payload.onProgress && progress_event.total) {
                            payload.onProgress(Math.round((progress_event.loaded * 100) / progress_event.total));
                        }
                    }
                });
                commit('UPDATE_CUSTOM_FONT', res.data.result);
                return res;
            } catch (error) {
                commit('SET_CUSTOM_FONTS_ERROR', error);
                throw error;
            }
        },
        /*
        * payload: { id, is_global, company_ids, force }
        * Replaces the whole attachment set of a font. A 409 means at least one company that would
        * be detached still has designs / carts / orders on the font; `error.response.data.result
        * .conflicts` carries the per company usage and the same call with force: true goes through.
        * */
        async syncCustomFontCompanies({ commit }, payload) {
            commit('SET_CUSTOM_FONTS_ERROR', null);
            try {
                let res = await http.put(`custom-fonts/${payload.id}/companies`, {
                    is_global: !!payload.is_global,
                    company_ids: Array.isArray(payload.company_ids) ? payload.company_ids : [],
                    force: !!payload.force,
                });
                commit('UPDATE_CUSTOM_FONT', res.data.result);
                return res;
            } catch (error) {
                commit('SET_CUSTOM_FONTS_ERROR', error);
                throw error;
            }
        },
        /*
        * payload: { id, company_id }
        * Read only lookup of what a font is used by, used to fill the confirmation dialogs before
        * a destructive action. Deliberately does not touch the state - the counts are not part of
        * the listing payload lifecycle and would be stale the moment the dialog closes.
        * */
        async fetchCustomFontUsage({ commit }, payload) {
            commit('SET_CUSTOM_FONTS_ERROR', null);
            try {
                let res = await http.get(`custom-fonts/${payload.id}/usage`, {
                    params: payload.company_id ? { company_id: payload.company_id } : {}
                });
                return res.data.result ? res.data.result : null;
            } catch (error) {
                commit('SET_CUSTOM_FONTS_ERROR', error);
                throw error;
            }
        },
        /*
        * payload: { id, force } - or a bare id, which some older callers still pass.
        * Answers 409 with `result.conflicts` when the font is still in use and force is falsy.
        * */
        async deleteCustomFont({ commit }, payload) {
            const id = payload && typeof payload === 'object' ? payload.id : payload;
            const force = payload && typeof payload === 'object' ? payload.force : false;
            commit('SET_CUSTOM_FONTS_ERROR', null);
            try {
                let res = await http.delete(`custom-fonts/${id}`, {
                    params: force ? { force: 1 } : {}
                });
                commit('DELETE_CUSTOM_FONT', id);
                return res;
            } catch (error) {
                commit('SET_CUSTOM_FONTS_ERROR', error);
                throw error;
            }
        },
    },
    getters: {
        getCustomFonts(state) {
            return state.customFonts;
        },
        getCustomFontsLoading(state) {
            return state.customFontsLoading;
        },
        getCustomFontsError(state) {
            return state.customFontsError;
        },
    }
}
